import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ClockIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  FireIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface SmartMetric {
  id: string;
  title: string;
  value: number;
  unit: string;
  change: number;
  changeType: 'increase' | 'decrease';
  trend: 'positive' | 'negative' | 'neutral';
  aiInsight: string;
  confidence: number;
  category: 'financial' | 'operational' | 'marketing' | 'risk';
  status: 'excellent' | 'good' | 'warning' | 'critical';
  benchmark: number;
  target: number;
  lastUpdated: Date;
}

interface SmartMetricsProps {
  entityId: string;
  entityType: string;
  timeframe?: '24h' | '7d' | '30d' | '90d';
}

const SmartMetrics: React.FC<SmartMetricsProps> = ({
  entityId,
  entityType,
  timeframe = '30d'
}) => {
  const { t, direction } = useLanguage();
  const [metrics, setMetrics] = useState<SmartMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    // Simulate AI-generated metrics
    const generateSmartMetrics = (): SmartMetric[] => {
      return [
        {
          id: '1',
          title: 'كفاءة الإنفاق الذكية',
          value: 87.5,
          unit: '%',
          change: 12.3,
          changeType: 'increase',
          trend: 'positive',
          aiInsight: 'تحسن ملحوظ في كفاءة الإنفاق بفضل تطبيق التوصيات الذكية. متوقع وصول 95% خلال الشهر القادم.',
          confidence: 94,
          category: 'financial',
          status: 'excellent',
          benchmark: 75,
          target: 90,
          lastUpdated: new Date(Date.now() - 5 * 60 * 1000)
        },
        {
          id: '2',
          title: 'معدل العائد على الاستثمار',
          value: 3.2,
          unit: 'x',
          change: 0.8,
          changeType: 'increase',
          trend: 'positive',
          aiInsight: 'العائد على الاستثمار يفوق المعايير الصناعية بنسبة 45%. الاستراتيجية الحالية تحقق نتائج استثنائية.',
          confidence: 91,
          category: 'financial',
          status: 'excellent',
          benchmark: 2.1,
          target: 3.5,
          lastUpdated: new Date(Date.now() - 10 * 60 * 1000)
        },
        {
          id: '3',
          title: 'مؤشر رضا العملاء الذكي',
          value: 4.7,
          unit: '/5',
          change: 0.3,
          changeType: 'increase',
          trend: 'positive',
          aiInsight: 'تحليل المشاعر يشير إلى تحسن كبير في رضا العملاء. التوقعات تشير لوصول 4.9 خلال الربع القادم.',
          confidence: 88,
          category: 'operational',
          status: 'good',
          benchmark: 4.2,
          target: 4.8,
          lastUpdated: new Date(Date.now() - 15 * 60 * 1000)
        },
        {
          id: '4',
          title: 'معدل التحويل المحسن',
          value: 18.5,
          unit: '%',
          change: 5.2,
          changeType: 'increase',
          trend: 'positive',
          aiInsight: 'تحسينات الذكاء الاصطناعي على موقع الويب أدت لزيادة معدل التحويل بشكل كبير.',
          confidence: 92,
          category: 'marketing',
          status: 'excellent',
          benchmark: 12.3,
          target: 20,
          lastUpdated: new Date(Date.now() - 8 * 60 * 1000)
        },
        {
          id: '5',
          title: 'مؤشر المخاطر الذكي',
          value: 2.1,
          unit: '/10',
          change: -0.8,
          changeType: 'decrease',
          trend: 'positive',
          aiInsight: 'انخفاض مستوى المخاطر بفضل تطبيق استراتيجيات التخفيف المقترحة من الذكاء الاصطناعي.',
          confidence: 96,
          category: 'risk',
          status: 'excellent',
          benchmark: 3.5,
          target: 2.0,
          lastUpdated: new Date(Date.now() - 3 * 60 * 1000)
        },
        {
          id: '6',
          title: 'سرعة الاستجابة التشغيلية',
          value: 1.2,
          unit: 'ساعة',
          change: -0.5,
          changeType: 'decrease',
          trend: 'positive',
          aiInsight: 'تحسن كبير في أوقات الاستجابة بفضل أتمتة العمليات والتحليل التنبؤي.',
          confidence: 89,
          category: 'operational',
          status: 'good',
          benchmark: 2.1,
          target: 1.0,
          lastUpdated: new Date(Date.now() - 12 * 60 * 1000)
        }
      ];
    };

    setTimeout(() => {
      setMetrics(generateSmartMetrics());
      setLoading(false);
    }, 1200);
  }, [entityId, entityType, timeframe]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'from-green-500 to-green-600';
      case 'good': return 'from-blue-500 to-blue-600';
      case 'warning': return 'from-yellow-500 to-orange-500';
      case 'critical': return 'from-red-500 to-red-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'excellent': return CheckCircleIcon;
      case 'good': return CheckCircleIcon;
      case 'warning': return ExclamationTriangleIcon;
      case 'critical': return ExclamationTriangleIcon;
      default: return ChartBarIcon;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'financial': return BanknotesIcon;
      case 'operational': return ChartBarIcon;
      case 'marketing': return FireIcon;
      case 'risk': return ExclamationTriangleIcon;
      default: return SparklesIcon;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'financial': return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      case 'operational': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400';
      case 'marketing': return 'text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400';
      case 'risk': return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const formatValue = (value: number, unit: string) => {
    if (unit === '%') return `${value.toFixed(1)}%`;
    if (unit === 'x') return `${value.toFixed(1)}x`;
    if (unit === '/5' || unit === '/10') return `${value.toFixed(1)}${unit}`;
    if (unit === 'ساعة') return `${value.toFixed(1)} ${unit}`;
    return `${value.toLocaleString()} ${unit}`;
  };

  const getProgressPercentage = (value: number, target: number) => {
    return Math.min((value / target) * 100, 100);
  };

  const filteredMetrics = selectedCategory === 'all' 
    ? metrics 
    : metrics.filter(metric => metric.category === selectedCategory);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
              <SparklesIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                المؤشرات الذكية
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                تحليل متقدم بالذكاء الاصطناعي
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">جميع المؤشرات</option>
              <option value="financial">مالية</option>
              <option value="operational">تشغيلية</option>
              <option value="marketing">تسويقية</option>
              <option value="risk">مخاطر</option>
            </select>
            
            <button className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <EyeIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMetrics.map((metric, index) => {
            const StatusIcon = getStatusIcon(metric.status);
            const CategoryIcon = getCategoryIcon(metric.category);
            const progressPercentage = getProgressPercentage(metric.value, metric.target);
            
            return (
              <motion.div
                key={metric.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 hover:shadow-md transition-all duration-300"
              >
                {/* Metric Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <div className={`p-2 bg-gradient-to-r ${getStatusColor(metric.status)} rounded-lg`}>
                      <CategoryIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {metric.title}
                      </h4>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getCategoryColor(metric.category)}`}>
                        {metric.category === 'financial' ? 'مالي' :
                         metric.category === 'operational' ? 'تشغيلي' :
                         metric.category === 'marketing' ? 'تسويقي' : 'مخاطر'}
                      </span>
                    </div>
                  </div>
                  
                  <StatusIcon className={`w-5 h-5 ${
                    metric.status === 'excellent' || metric.status === 'good' 
                      ? 'text-green-500' 
                      : metric.status === 'warning' 
                        ? 'text-yellow-500' 
                        : 'text-red-500'
                  }`} />
                </div>

                {/* Value and Change */}
                <div className="mb-3">
                  <div className="flex items-baseline space-x-2 rtl:space-x-reverse">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatValue(metric.value, metric.unit)}
                    </span>
                    <div className={`flex items-center text-sm font-medium ${
                      metric.trend === 'positive' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {metric.trend === 'positive' ? (
                        <ArrowTrendingUpIcon className="w-4 h-4 ml-1 rtl:ml-0 rtl:mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="w-4 h-4 ml-1 rtl:ml-0 rtl:mr-1" />
                      )}
                      {metric.changeType === 'increase' ? '+' : ''}{metric.change.toFixed(1)}
                      {metric.unit === '%' ? 'نقطة' : metric.unit}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span>التقدم نحو الهدف</span>
                    <span>{progressPercentage.toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <motion.div
                      className={`bg-gradient-to-r ${getStatusColor(metric.status)} h-2 rounded-full`}
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercentage}%` }}
                      transition={{ duration: 1, delay: index * 0.1 }}
                    />
                  </div>
                </div>

                {/* AI Insight */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-3">
                  <div className="flex items-start space-x-2 rtl:space-x-reverse">
                    <SparklesIcon className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                        {metric.aiInsight}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                          ثقة الذكاء الاصطناعي: {metric.confidence}%
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                          <ClockIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1" />
                          منذ {Math.floor((Date.now() - metric.lastUpdated.getTime()) / 60000)} دقيقة
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Benchmark Comparison */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white dark:bg-gray-800 rounded p-2 text-center">
                    <div className="text-gray-500 dark:text-gray-400">المعيار</div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {formatValue(metric.benchmark, metric.unit)}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded p-2 text-center">
                    <div className="text-gray-500 dark:text-gray-400">الهدف</div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {formatValue(metric.target, metric.unit)}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SmartMetrics;
